import React, { useState, useEffect } from 'react';
import { apiGet } from '../../utils/api';
import { Card, Spinner, Badge } from '../design-system';
import { Users, UserPlus, TrendingUp, CheckCircle, Clock, BarChart3, Activity } from 'lucide-react';

const PracticeAnalytics = ({ memberCount = 0, patientCount = 0 }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet('/.netlify/functions/get-practice-analytics');
      setAnalytics(response);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0 min';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Card variant="default" padding="large">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  if (!analytics) {
    return (
      <Card variant="default" padding="large">
        <div className="text-center py-8">
          <p className="text-gray-600">No analytics data available</p>
        </div>
      </Card>
    );
  }

  const { overall, therapists } = analytics;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Overall Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card variant="default" padding="medium">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="p-2 md:p-3 bg-blue-100 rounded-lg flex-shrink-0">
              <Users className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs md:text-sm font-medium text-gray-600 truncate">Members</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
                {memberCount}
              </p>
            </div>
          </div>
        </Card>

        <Card variant="default" padding="medium">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="p-2 md:p-3 bg-green-100 rounded-lg flex-shrink-0">
              <Activity className="h-5 w-5 md:h-6 md:w-6 text-green-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs md:text-sm font-medium text-gray-600 truncate">Total Patients</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
                {patientCount}
              </p>
            </div>
          </div>
        </Card>

        <Card variant="default" padding="medium">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="p-2 md:p-3 bg-green-100 rounded-lg flex-shrink-0">
              <UserPlus className="h-5 w-5 md:h-6 md:w-6 text-green-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs md:text-sm font-medium text-gray-600 truncate">New Patients (30d)</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
                {overall.newPatients || 0}
              </p>
            </div>
          </div>
        </Card>

        <Card variant="default" padding="medium">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="p-2 md:p-3 bg-purple-100 rounded-lg flex-shrink-0">
              <TrendingUp className="h-5 w-5 md:h-6 md:w-6 text-purple-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs md:text-sm font-medium text-gray-600 truncate">Retention Rate</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
                {overall.retentionRate || 0}%
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Task Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        <Card variant="default" padding="medium">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="p-2 md:p-3 bg-indigo-100 rounded-lg flex-shrink-0">
              <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-indigo-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs md:text-sm font-medium text-gray-600 truncate">Total Tasks</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
                {overall.totalTasks || 0}
              </p>
            </div>
          </div>
        </Card>

        <Card variant="default" padding="medium">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="p-2 md:p-3 bg-teal-100 rounded-lg flex-shrink-0">
              <BarChart3 className="h-5 w-5 md:h-6 md:w-6 text-teal-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs md:text-sm font-medium text-gray-600 truncate">Completion Rate</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
                {overall.taskCompletionRate || 0}%
              </p>
            </div>
          </div>
        </Card>

        <Card variant="default" padding="medium">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="p-2 md:p-3 bg-orange-100 rounded-lg flex-shrink-0">
              <Clock className="h-5 w-5 md:h-6 md:w-6 text-orange-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs md:text-sm font-medium text-gray-600 truncate">Activity Time</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
                {formatTime(overall.totalActivityTime || 0)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Therapist Breakdown */}
      <Card variant="elevated" padding="medium md:padding-large">
        <div className="mb-4 md:mb-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1">
            Therapist Performance
          </h3>
          <p className="text-xs md:text-sm text-gray-600">
            Individual metrics for each therapist in your practice
          </p>
        </div>
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <div className="inline-block min-w-full align-middle px-4 md:px-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Therapist
                  </th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Patients
                  </th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                    New (30d)
                  </th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Retention
                  </th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Tasks
                  </th>
                  <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                    Activity Time
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {therapists && therapists.length > 0 ? (
                  therapists.map((therapist) => (
                    <tr key={therapist.therapistId} className="hover:bg-gray-50 transition-colors">
                      <td className="px-2 md:px-4 py-3 md:py-4">
                        <div className="min-w-0">
                          <div className="text-xs md:text-sm font-semibold text-gray-900 truncate">
                            {therapist.therapistName}
                          </div>
                          <div className="text-xs md:text-sm text-gray-500 truncate hidden sm:block">
                            {therapist.therapistEmail}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 md:px-4 py-3 md:py-4 whitespace-nowrap">
                        <div className="text-xs md:text-sm font-medium text-gray-900">
                          {therapist.totalPatients || 0}
                        </div>
                      </td>
                      <td className="px-2 md:px-4 py-3 md:py-4 whitespace-nowrap hidden sm:table-cell">
                        <div className="text-xs md:text-sm font-medium text-gray-900">
                          {therapist.newPatients || 0}
                        </div>
                      </td>
                      <td className="px-2 md:px-4 py-3 md:py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 md:gap-2">
                          <div className="text-xs md:text-sm font-medium text-gray-900">
                            {therapist.retentionRate || 0}%
                          </div>
                          {therapist.retentionRate >= 70 ? (
                            <Badge variant="success" size="small" className="hidden md:inline-flex">Good</Badge>
                          ) : therapist.retentionRate >= 50 ? (
                            <Badge variant="default" size="small" className="hidden md:inline-flex">Fair</Badge>
                          ) : (
                            <Badge variant="error" size="small" className="hidden md:inline-flex">Low</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-2 md:px-4 py-3 md:py-4 whitespace-nowrap hidden md:table-cell">
                        <div className="text-xs md:text-sm font-medium text-gray-900">
                          {therapist.totalTasks || 0}
                          {therapist.totalTasks > 0 && (
                            <span className="text-gray-500 ml-1 text-xs hidden lg:inline">
                              ({therapist.taskCompletionRate}% done)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 md:px-4 py-3 md:py-4 whitespace-nowrap hidden lg:table-cell">
                        <div className="text-xs md:text-sm font-medium text-gray-900">
                          {formatTime(therapist.totalActivityTime || 0)}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 md:py-12 text-center">
                      <div className="text-gray-500">
                        <Activity className="h-8 w-8 md:h-12 md:w-12 mx-auto mb-2 md:mb-3 text-gray-400" />
                        <p className="text-sm md:text-base">No therapist data available</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PracticeAnalytics;
